import React, { useEffect, useRef, useState } from "react";
import { CCard, CCardBody, CButton, CSpinner } from "@coreui/react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import * as echarts from "echarts";
import { getDashboardQuotationFunnel } from "../../utils/backend";

const EMPTY_TOTALS = {
  total: 0,
  replied: 0,
  purchased: 0,
  noCustomerReply: 0,
  waitingCustomer: 0,
  waitingInsurer: 0,
};

const COUNTERS = [
  { key: "total", label: "Total Requests", color: "#1E40AF" },
  { key: "replied", label: "Replied to Customer", color: "#16A34A" },
  { key: "purchased", label: "Successful Purchases", color: "#0D9488" },
  { key: "noCustomerReply", label: "No Reply from Customer", color: "#DC2626" },
  { key: "waitingCustomer", label: "Waiting for Customer", color: "#F97316" },
  { key: "waitingInsurer", label: "Waiting for Insurer", color: "#EAB308" },
];

const getDefaultDateRange = () => {
  const start = new Date();
  start.setMonth(start.getMonth() - 5);
  start.setDate(1);
  start.setHours(0, 0, 0, 0);

  const end = new Date();
  end.setMonth(end.getMonth() + 1);
  end.setDate(0);
  end.setHours(23, 59, 59, 999);

  return [start, end];
};

/**
 * One quotation-funnel widget (ACS Dashboard BRD sections 1, 2 and 4).
 * `funnel` is api_vehicle | manual_vehicle | manual_inquiry.
 */
const QuotationFunnelCard = ({ title, funnel }) => {
  const seriesChartRef = useRef(null);
  const insurerChartRef = useRef(null);
  const dropdownRef = useRef(null);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [activeButton, setActiveButton] = useState(null);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [byInsurer, setByInsurer] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);

      let [startDate, endDate] = dateRange || [null, null];

      if (!startDate || !endDate) {
        const today = new Date();
        startDate = today;
        endDate = today;
      }

      const formatDate = (date) => date.toLocaleDateString("en-CA");

      const response = await getDashboardQuotationFunnel({
        funnel,
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      });

      setTotals(response.data.totals || EMPTY_TOTALS);
      setByInsurer(response.data.byInsurer || []);
      setSeries(response.data.series || []);
    } catch (error) {
      console.error(`Error fetching quotation funnel (${funnel}):`, error);
      setTotals(EMPTY_TOTALS);
      setByInsurer([]);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      fetchData();
    } else {
      setDateRange([new Date(), new Date()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  // Requests-over-time chart. Days collapse into months for long ranges so
  // the axis stays readable.
  useEffect(() => {
    if (!seriesChartRef.current) return;

    const monthly = series.length > 62;
    const buckets = series.reduce((acc, item) => {
      const label = monthly
        ? new Date(item.date).toLocaleString("default", {
            month: "short",
            year: "numeric",
          })
        : new Date(item.date).toLocaleString("default", {
            day: "2-digit",
            month: "short",
          });
      acc[label] = (acc[label] || 0) + item.total;
      return acc;
    }, {});

    const chartInstance = echarts.init(seriesChartRef.current);
    chartInstance.setOption({
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      grid: { left: "4%", right: "5%", top: "10%", bottom: "12%", containLabel: true },
      xAxis: { type: "category", data: Object.keys(buckets) },
      yAxis: { type: "value", minInterval: 1 },
      series: [
        {
          type: "bar",
          name: "Requests",
          data: Object.values(buckets),
          itemStyle: { color: "#1E40AF", barBorderRadius: [10, 10, 0, 0] },
          barWidth: "40%",
        },
      ],
    });

    return () => {
      chartInstance.dispose();
    };
  }, [series]);

  // Transactions by insurance company chart.
  useEffect(() => {
    if (!insurerChartRef.current) return;

    const top = byInsurer.slice(0, 10).reverse();

    const chartInstance = echarts.init(insurerChartRef.current);
    chartInstance.setOption({
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
      grid: { left: "4%", right: "8%", top: "5%", bottom: "5%", containLabel: true },
      xAxis: { type: "value", minInterval: 1 },
      yAxis: {
        type: "category",
        data: top.map((item) => item.insurer_name),
        axisLabel: {
          width: 170,
          overflow: "truncate",
        },
      },
      series: [
        {
          type: "bar",
          name: "Transactions",
          data: top.map((item) => item.total),
          itemStyle: { color: "#16A34A", barBorderRadius: [0, 10, 10, 0] },
          barWidth: "50%",
        },
      ],
    });

    return () => {
      chartInstance.dispose();
    };
  }, [byInsurer]);

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setTempDateRange(selectedDates);
      setActiveButton(null);
    }
    setDropdownVisible(true);
  };

  const applyDateRange = () => {
    setDateRange(tempDateRange);
    setDropdownVisible(false);
  };

  const setDateRangeToToday = () => {
    const today = new Date();
    setTempDateRange([today, today]);
  };

  const setDateRangeToThisWeek = () => {
    const today = new Date();
    const firstDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay())
    );
    const lastDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay() + 6)
    );
    setTempDateRange([firstDayOfWeek, lastDayOfWeek]);
  };

  const setDateRangeToThisMonth = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );
    setTempDateRange([firstDayOfMonth, lastDayOfMonth]);
  };

  const resetDateRange = () => {
    setTempDateRange(getDefaultDateRange());
  };

  const handleButtonClick = (rangeSetter, buttonId) => {
    rangeSetter();
    setActiveButton(buttonId);
  };

  const formatDateRange = (range) => {
    if (range.length === 2) {
      return `${range[0]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")} - ${range[1]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")}`.toUpperCase();
    }
    return "Select Date Range";
  };

  return (
    <CCard className="border-light">
      <CCardBody style={{ overflowX: "auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span>{title}</span>
          <div
            ref={dropdownRef}
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "4px",
              borderRadius: "5px",
              width: "300px",
              position: "relative",
            }}
          >
            <CButton
              style={{
                border: "0px solid lightgrey",
                backgroundColor: "#F8F5F5",
                borderRadius: "5px",
                color: "#343333",
                height: "45px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                transition: "background-color 0.3s",
              }}
              color="primary"
              onClick={() => setDropdownVisible(!dropdownVisible)}
            >
              {formatDateRange(dateRange)}
              <i
                className={`bi bi-chevron-down ${
                  dropdownVisible ? "rotate" : ""
                }`}
                style={{ marginLeft: "10px" }}
              ></i>
            </CButton>
            {dropdownVisible && (
              <CCard
                className="border-light"
                style={{
                  position: "absolute",
                  top: "50px",
                  right: 0,
                  zIndex: 1000,
                  width: "300px",
                }}
              >
                <CCardBody style={{ overflowX: "auto" }}>
                  <span>Filter By Date Range</span>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-start",
                      padding: "8px",
                      borderRadius: "5px",
                      width: "100%",
                      margin: "0px auto",
                      position: "relative",
                    }}
                  >
                    <Flatpickr
                      options={{ mode: "range", dateFormat: "d/m/Y" }}
                      value={tempDateRange}
                      onChange={handleDateChange}
                      className="form-control"
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor: "white",
                        color: "#555E67",
                        borderRadius: "10px",
                        width: "100%",
                        height: "35px",
                      }}
                    />
                    <i
                      className={`bi bi-calendar`}
                      style={{
                        position: "absolute",
                        right: "20px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    ></i>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      marginTop: "0px",
                    }}
                  >
                    <CButton
                      color="secondary"
                      onClick={() =>
                        handleButtonClick(setDateRangeToToday, "today")
                      }
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor:
                          activeButton === "today" ? "#0B4FE4" : "white",
                        borderRadius: "10px",
                        color: activeButton === "today" ? "white" : "#555E67",
                        width: "30%",
                        height: "60px",
                        cursor: "pointer",
                      }}
                    >
                      Today
                    </CButton>
                    <CButton
                      color="secondary"
                      onClick={() =>
                        handleButtonClick(setDateRangeToThisWeek, "thisWeek")
                      }
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor:
                          activeButton === "thisWeek" ? "#0B4FE4" : "white",
                        borderRadius: "10px",
                        color:
                          activeButton === "thisWeek" ? "white" : "#555E67",
                        width: "30%",
                        height: "60px",
                        cursor: "pointer",
                      }}
                    >
                      This Week
                    </CButton>
                    <CButton
                      color="secondary"
                      onClick={() =>
                        handleButtonClick(setDateRangeToThisMonth, "thisMonth")
                      }
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor:
                          activeButton === "thisMonth" ? "#0B4FE4" : "white",
                        borderRadius: "10px",
                        color:
                          activeButton === "thisMonth" ? "white" : "#555E67",
                        width: "30%",
                        height: "60px",
                        cursor: "pointer",
                      }}
                    >
                      This Month
                    </CButton>
                  </div>
                  <hr></hr>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "10px",
                    }}
                  >
                    <CButton
                      color="secondary"
                      onClick={() => {
                        resetDateRange();
                        setActiveButton(null);
                      }}
                      style={{
                        border: "1px solid #0B4FE4",
                        backgroundColor: "white",
                        borderRadius: "5px",
                        color: "#0B4FE4",
                        width: "45%",
                        height: "40px",
                        cursor: "pointer",
                      }}
                    >
                      Reset
                    </CButton>
                    <CButton
                      color="primary"
                      onClick={applyDateRange}
                      style={{
                        border: "1px solid lightgrey",
                        backgroundColor: "#0B4FE4",
                        borderRadius: "5px",
                        color: "white",
                        width: "45%",
                        height: "40px",
                        cursor: "pointer",
                      }}
                    >
                      Apply
                    </CButton>
                  </div>
                </CCardBody>
              </CCard>
            )}
          </div>
        </div>

        <div className="p-2">
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "120px",
              }}
            >
              <CSpinner color="primary" />
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "10px",
                marginBottom: "20px",
              }}
            >
              {COUNTERS.map((counter) => (
                <div
                  key={counter.key}
                  style={{
                    backgroundColor: "#F8F5F5",
                    borderRadius: "10px",
                    padding: "12px",
                    borderLeft: `4px solid ${counter.color}`,
                  }}
                >
                  <h4 style={{ marginBottom: "2px" }}>
                    <strong>{totals[counter.key] ?? 0}</strong>
                  </h4>
                  <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                    {counter.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
            className="quotation-funnel-charts"
          >
            <div>
              <span className="text-muted">Total Requests by Day / Month</span>
              <div ref={seriesChartRef} style={{ height: "300px" }}></div>
            </div>
            <div>
              <span className="text-muted">
                Transactions by Insurance Company
              </span>
              {byInsurer.length === 0 && !loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "300px",
                    color: "#8a93a2",
                  }}
                >
                  No transactions in this period
                </div>
              ) : (
                <div ref={insurerChartRef} style={{ height: "300px" }}></div>
              )}
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 768px) {
            .quotation-funnel-charts {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </CCardBody>
    </CCard>
  );
};

export default QuotationFunnelCard;
